import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FileText, Upload, Download, Eye, Archive, Loader2, X, Plus } from "lucide-react";
import api from "@/lib/api";

const CATEGORIES = [
  { value: "imaging", label: "Imaging / MRI" },
  { value: "gp_referral", label: "GP Referral" },
  { value: "consultant_letter", label: "Consultant Letter" },
  { value: "hospital_discharge", label: "Hospital / Discharge" },
  { value: "investigation_result", label: "Investigation / Result" },
  { value: "clinical_correspondence", label: "Clinical Correspondence" },
  { value: "other", label: "Other" },
];
const CAT_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));
const ACCEPT = ".pdf,.jpg,.jpeg,.png,.doc,.docx";

function fmtSize(b) {
  if (!b) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
}

export default function PatientInvestigations({ patientId, patientName }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [viewer, setViewer] = useState(null); // { url, mime, title }
  const fileRef = useRef(null);
  const [form, setForm] = useState({ title: "", category: "imaging", document_date: "", source: "", note: "" });

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/patients/${patientId}/documents`);
      setDocs(r.data || []);
    } catch { toast.error("Could not load documents"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [patientId]);

  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) return toast.error("Choose a file to upload");
    const fd = new FormData();
    fd.append("file", f);
    fd.append("title", form.title || f.name);
    fd.append("category", form.category);
    fd.append("document_date", form.document_date);
    fd.append("source", form.source);
    fd.append("note", form.note);
    setUploading(true);
    try {
      await api.post(`/patients/${patientId}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document attached to " + (patientName || "patient"));
      setForm({ title: "", category: "imaging", document_date: "", source: "", note: "" });
      if (fileRef.current) fileRef.current.value = "";
      setShowForm(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const fetchBlob = async (d, disposition) => {
    const r = await api.get(`/patients/${patientId}/documents/${d.id}/file?disposition=${disposition}`, { responseType: "blob" });
    return URL.createObjectURL(r.data);
  };

  const view = async (d) => {
    try {
      const url = await fetchBlob(d, "inline");
      setViewer({ url, mime: d.mime_type, title: d.title });
    } catch { toast.error("Could not open document"); }
  };

  const download = async (d) => {
    try {
      const url = await fetchBlob(d, "attachment");
      const a = document.createElement("a");
      a.href = url; a.download = d.original_filename || d.title || "document";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch { toast.error("Could not download document"); }
  };

  const archive = async (d) => {
    if (!window.confirm(`Archive "${d.title}"? It will be removed from the active list but kept for the audit record.`)) return;
    try {
      await api.delete(`/patients/${patientId}/documents/${d.id}`);
      toast.success("Document archived");
      load();
    } catch { toast.error("Could not archive document"); }
  };

  return (
    <div className="fl-card fl-card-elevated p-6 mt-4" data-testid="investigations-files-card">
      <div className="flex items-center justify-between gap-3 mb-1">
        <div className="fl-heading text-xl flex items-center gap-2"><FileText size={18} /> Investigations &amp; Files</div>
        <button className="fl-btn text-sm inline-flex items-center gap-2" onClick={() => setShowForm((v) => !v)} data-testid="inv-add-toggle">
          <Plus size={14} /> Add document
        </button>
      </div>
      <div className="text-xs text-zinc-500 mb-4">MRI/imaging reports, GP referrals, consultant &amp; discharge letters, results and clinical correspondence. These are clinical records — not questionnaires.</div>

      {showForm && (
        <div className="rounded-lg p-4 mb-4" style={{ border: "1px solid var(--fl-border)", background: "var(--fl-bg-2)" }} data-testid="inv-upload-form">
          <div className="grid md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <div className="fl-label mb-1">File (PDF, JPG, PNG, DOC/DOCX · 30 MB max)</div>
              <input ref={fileRef} type="file" accept={ACCEPT} className="fl-input" data-testid="inv-file-input" />
            </div>
            <div>
              <div className="fl-label mb-1">Title</div>
              <input className="fl-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Right ankle MRI report" data-testid="inv-title" />
            </div>
            <div>
              <div className="fl-label mb-1">Category</div>
              <select className="fl-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="inv-category">
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <div className="fl-label mb-1">Document date (optional)</div>
              <input type="date" className="fl-input" value={form.document_date} onChange={(e) => setForm({ ...form, document_date: e.target.value })} data-testid="inv-doc-date" />
            </div>
            <div>
              <div className="fl-label mb-1">Source / organisation (optional)</div>
              <input className="fl-input" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="e.g. Spire Hospital" data-testid="inv-source" />
            </div>
            <div className="md:col-span-2">
              <div className="fl-label mb-1">Note (optional)</div>
              <textarea rows={2} className="fl-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="inv-note" />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button className="fl-btn-ghost text-sm" onClick={() => setShowForm(false)}>Cancel</button>
            <button className="fl-btn text-sm inline-flex items-center gap-2" onClick={upload} disabled={uploading} data-testid="inv-upload-submit">
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-zinc-500 text-sm flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading…</div>
      ) : docs.length === 0 ? (
        <div className="text-zinc-500 text-sm" data-testid="inv-empty">No documents yet.</div>
      ) : (
        <div className="space-y-2" data-testid="inv-list">
          {docs.map((d) => (
            <div key={d.id} className="flex items-start justify-between gap-3 p-3 rounded-md" style={{ border: "1px solid var(--fl-border)" }} data-testid={`inv-row-${d.id}`}>
              <div className="min-w-0">
                <div className="text-sm text-white font-medium truncate">{d.title}</div>
                <div className="text-xs text-zinc-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                  <span className="text-[#f47a20]">{CAT_LABEL[d.category] || d.category}</span>
                  <span>Doc date: {fmtDate(d.document_date)}</span>
                  <span>Uploaded: {fmtDate(d.uploaded_at)}</span>
                  {d.uploaded_by_name && <span>by {d.uploaded_by_name}</span>}
                  {d.source && <span>· {d.source}</span>}
                  <span>· {d.ext?.toUpperCase()} {fmtSize(d.size_bytes)}</span>
                </div>
                {d.note && <div className="text-xs text-zinc-400 mt-1">{d.note}</div>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button className="fl-btn-ghost text-xs inline-flex items-center gap-1" onClick={() => view(d)} data-testid={`inv-view-${d.id}`}><Eye size={13} /> View</button>
                <button className="fl-btn-ghost text-xs inline-flex items-center gap-1" onClick={() => download(d)} data-testid={`inv-download-${d.id}`}><Download size={13} /></button>
                <button className="fl-btn-ghost text-xs inline-flex items-center gap-1 text-red-400" onClick={() => archive(d)} data-testid={`inv-archive-${d.id}`}><Archive size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {viewer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.75)" }} onClick={() => { URL.revokeObjectURL(viewer.url); setViewer(null); }} data-testid="inv-viewer">
          <div className="bg-[#0f0f0f] rounded-lg w-full max-w-4xl h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} style={{ border: "1px solid var(--fl-border)" }}>
            <div className="flex items-center justify-between p-3 border-b border-white/10">
              <div className="text-sm text-white truncate">{viewer.title}</div>
              <div className="flex items-center gap-2">
                <a href={viewer.url} target="_blank" rel="noreferrer" className="fl-btn-ghost text-xs">Open in new tab</a>
                <button onClick={() => { URL.revokeObjectURL(viewer.url); setViewer(null); }} className="text-zinc-400 hover:text-white" data-testid="inv-viewer-close"><X size={18} /></button>
              </div>
            </div>
            <div className="flex-1 overflow-auto bg-black/40 flex items-center justify-center">
              {viewer.mime?.startsWith("image/") ? (
                <img src={viewer.url} alt={viewer.title} className="max-w-full max-h-full object-contain" />
              ) : viewer.mime === "application/pdf" ? (
                <iframe src={viewer.url} title={viewer.title} className="w-full h-full" />
              ) : (
                <div className="text-center text-zinc-400 text-sm p-8">
                  Preview isn't available for this file type.<br />
                  <a href={viewer.url} target="_blank" rel="noreferrer" className="text-[#f47a20] underline">Open / download instead</a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
